import {
    isMemberProfileDataComplete,
    type MembershipProfileKidAnswer,
    type MembershipProfileMoneyGoalAnswer,
    type MembershipProfileRetirementAnswer,
    type ProfileCompleteness,
    QuestionKey,
    UserType,
} from '@fruitful/member-profile';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { transformUserToMemberProfileMembership } from '../utils/memberProfileUtils';
import useGetMembershipIncomes from './useGetMembershipIncomes';
import useGetMembershipProfileAnswers from './useGetMembershipProfileAnswers';

interface GetProfileCompletenessResponse extends ProfileCompleteness {
    isLoading: boolean;
}

const useGetProfileDataCompleteness = (): GetProfileCompletenessResponse => {
    const user = useAuthedUser();
    const { users } = transformUserToMemberProfileMembership(user);

    const primaryUser = users.find(
        ({ type }) => type === UserType.PRIMARY_MEMBER,
    );
    const partnerUser = users.find(
        ({ type }) => type === UserType.PARTNER_MEMBER,
    );

    const { data: membershipIncomes, isLoading: isLoadingMembershipIncomes } =
        useGetMembershipIncomes({});

    // Don't include category param on these calls to be able to hit the query cache
    const { data: kids, isLoading: isLoadingKidsAnswer } =
        useGetMembershipProfileAnswers<MembershipProfileKidAnswer>({
            questionKey: QuestionKey.KIDS,
        });

    const { data: financialGoals, isLoading: isLoadingMoneyGoalsAnswer } =
        useGetMembershipProfileAnswers<MembershipProfileMoneyGoalAnswer[]>({
            questionKey: QuestionKey.MONEY_GOALS,
        });

    const { data: retirement, isLoading: isLoadingRetirementAnswer } =
        useGetMembershipProfileAnswers<MembershipProfileRetirementAnswer>({
            questionKey: QuestionKey.RETIREMENT,
        });

    const isLoading =
        isLoadingMembershipIncomes ||
        isLoadingKidsAnswer ||
        isLoadingMoneyGoalsAnswer ||
        isLoadingRetirementAnswer;

    const profileCompleteness: ProfileCompleteness =
        primaryUser != null
            ? isMemberProfileDataComplete(
                  primaryUser,
                  partnerUser ?? null,
                  membershipIncomes ?? [],
                  kids,
                  financialGoals,
                  retirement,
              )
            : {
                  isPersonalInfoComplete: false,
                  isFinancialGoalsComplete: false,
                  isIncomeComplete: false,
                  isProfileComplete: false,
                  isKidsDataComplete: false,
                  isEmploymentStatusComplete: false,
                  isInsuranceDataComplete: false,
                  isTaxDataComplete: false,
              };

    return {
        isLoading,
        ...profileCompleteness,
    };
};

export default useGetProfileDataCompleteness;
